import React, { useState } from 'react'
import { Typography, Box, Button, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Search from 'packages/eid-ui/GenericHeader/HeaderSearch'
import { Icon } from 'packages/eid-icons'
import { Tooltip } from 'packages/eid-ui'
const HeaderSearch = styled(Box)(({ theme }) => ({
    boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
    border: 'solid 0.1rem #ebebed',
    borderRadius: '0.4rem',
    maxWidth: '24rem',
    width: '100%',

    '&:hover, &:focus': {
        borderColor: theme.palette.primary.main,
    },

    '& input': {
        height: '2.7rem',
    },
    '& svg': {
        color: '#aab0b4',
        width: '1.8rem !important',
        height: '1.8rem !important',
    },
}))

const ExpandableList = ({
    items,
    visibleCount = 1, //minimum value should be 1
    headingLabel = '',
    showSearch = true,
    headingHelperText = '',
    maxCharacterLength = 30,
}) => {
    const [showAll, setShowAll] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const { t } = useTranslation()

    const highlightText = (text, highlight) => {
        if (!highlight) return text
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'))
        return parts.map((part, index) =>
            part.toLowerCase() === highlight.toLowerCase() ? (
                <span
                    key={index}
                    style={{ backgroundColor: 'yellow', margin: 0 }}
                >
                    {part}
                </span>
            ) : (
                part
            ),
        )
    }

    const truncateText = (text) => {
        if (text.length <= maxCharacterLength) return text
        return text.substring(0, maxCharacterLength) + '...'
    }

    const renderItemText = (item, searchTerm) => {
        const truncatedText = truncateText(
            item.friendlyName,
            maxCharacterLength,
        )
        const highlightedText = highlightText(truncatedText, searchTerm)

        // If text was truncated, wrap in tooltip
        if (item.friendlyName.length > maxCharacterLength) {
            return (
                <Tooltip title={item.friendlyName}>
                    <Box sx={{ display: 'inline' }}>{highlightedText}</Box>
                </Tooltip>
            )
        }

        return highlightedText
    }

    const handleInteractiveKeyDown = (event, action) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            action()
        }
    }

    const filteredItems = items.filter((item) =>
        item.friendlyName.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    const visibleItems = filteredItems.slice(0, visibleCount)
    const remainingItems = filteredItems.slice(visibleCount)

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                    {headingLabel && (
                        <Box sx={{ display: 'flex', gap: '2.2rem' }}>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1.6rem',
                                }}
                            >
                                {t(headingLabel)}
                            </Typography>
                            {headingHelperText && (
                                <span
                                    title={t(headingHelperText)}
                                    role="img"
                                    aria-label={headingHelperText}
                                    style={{
                                        cursor: 'pointer',
                                        marginTop: '0.5rem',
                                    }}
                                >
                                    <Icon name={'InfoNew'} aria-hidden="true" />
                                </span>
                            )}
                        </Box>
                    )}
                </Box>
                {showSearch && (
                    <HeaderSearch
                        sx={{
                            pointerEvents: 'auto',
                            opacity: 1,
                            cursor: 'auto',
                        }}
                    >
                        <Search
                            styleData={{}}
                            value={searchTerm}
                            handleSearch={(val) => setSearchTerm(val)}
                            placeholder={t('QuickSearch')}
                        />
                    </HeaderSearch>
                )}
            </Box>

            <Box display="flex" flexWrap="wrap" gap={'2.4rem'}>
                {searchTerm && filteredItems.length === 0 && (
                    <Typography sx={{ fontSize: '1.3rem' }}>
                        {t('NoDataFound')}
                    </Typography>
                )}
                {visibleItems.map((item, index) => (
                    <Box key={index} display="flex" alignItems="center">
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '1.3rem',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                }}
                            >
                                <Icon name={item?.iconName} />
                                <Box>{renderItemText(item, searchTerm)}</Box>
                            </Box>
                        </Typography>
                    </Box>
                ))}
                {!showAll && filteredItems.length > visibleItems.length && (
                    <Typography
                        component="span"
                        role="button"
                        tabIndex={0}
                        aria-label={`+${filteredItems.length - visibleItems.length} More`}
                        sx={{
                            fontSize: '1.2rem',
                            cursor: 'pointer',
                            color: '#0057A0',
                        }}
                        onClick={() => setShowAll(true)}
                        onKeyDown={(event) =>
                            handleInteractiveKeyDown(event, () =>
                                setShowAll(true),
                            )
                        }
                    >
                        {`+${filteredItems.length - visibleItems.length} ${t(
                            'More',
                        )}`}
                    </Typography>
                )}
                {showAll && filteredItems.length > visibleItems.length && (
                    <Typography
                        component="span"
                        role="button"
                        tabIndex={0}
                        aria-label="Show less"
                        sx={{
                            fontSize: '1.2rem',
                            cursor: 'pointer',
                            color: '#0057A0',
                        }}
                        onClick={() => setShowAll(false)}
                        onKeyDown={(event) =>
                            handleInteractiveKeyDown(event, () =>
                                setShowAll(false),
                            )
                        }
                    >
                        {t('ShowLess')}
                    </Typography>
                )}
            </Box>

            {showAll && remainingItems?.length > 0 && (
                <Box
                    mt={2}
                    p={2}
                    sx={{
                        width: '100%',
                        border: '1px solid #d8d8dd',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                    }}
                >
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {remainingItems.map((item, index) => (
                            <Box
                                key={index}
                                display="flex"
                                alignItems="center"
                                width="calc(25% - 16px)"
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 'normal',
                                        fontSize: '1.3rem',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Icon name={item?.iconName} />
                                        <Box>
                                            {renderItemText(item, searchTerm)}
                                        </Box>
                                    </Box>
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export default ExpandableList
